import { KitRelief } from '../inventory/kit.interface';   // F6c
// W3-O: mirrors SalesOrderDtos (backend sales/order/dto)
import { SalesInvoice } from './salesInvoice.interface';

export type SalesOrderStatus = 'DRAFT' | 'CONFIRMED' | 'PARTIALLY_DELIVERED' | 'DELIVERED' | 'CLOSED' | 'CANCELLED';
export type ChallanStatus = 'DRAFT' | 'POSTED' | 'CANCELLED';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const SALES_ORDER_STATUS_COLORS: Record<SalesOrderStatus, ChipColor> = {
  DRAFT: 'default', CONFIRMED: 'primary', PARTIALLY_DELIVERED: 'warning', DELIVERED: 'success', CLOSED: 'info', CANCELLED: 'error',
};
export const CHALLAN_STATUS_COLORS: Record<ChallanStatus, ChipColor> = {
  DRAFT: 'default', POSTED: 'success', CANCELLED: 'error',
};

export interface OrderLineRequest {
  id?: string;
  itemId?: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRatePct?: number;
  taxCodeId?: string | null;
  revenueAccountId?: string | null;
  segmentId?: string | null;
  storeId?: string | null;
  // ---- FW-2A ----
  discountPct?: number | null;
  discountAmount?: number | null;
  // ---- end FW-2A ----
}

export interface OrderRequest {
  customerId: string;
  orderDate: string;
  requestedDeliveryDate?: string | null;
  notes?: string;
  lines: OrderLineRequest[];
  /** G5b: omit (or BDT) for a BDT order; on a foreign one unit prices and amount discounts are in this currency. */
  currencyCode?: string;
  exchangeRate?: number | null;
  /** S0: owner of the order; omit for the default (the quotation's / customer's salesperson, else you). Naming someone else needs SALES_REASSIGN. */
  salespersonId?: string | null;
}

export interface OrderLine {
  id: string; lineNo: number; itemId?: string; itemCode?: string; itemName?: string; unit?: string; description: string;
  quantity: number; unitPrice: number; taxRatePct: number; taxCodeId?: string; lineTax: number; lineTotal: number;
  revenueAccountId?: string; segmentId?: string; storeId?: string;
  deliveredQty: number; invoicedQty: number; backOrderQty: number;
  /** FW-2A: lineTotal is the net after the discount. */
  grossAmount?: number | null; discountPct?: number | null; discountAmount?: number | null;
  /** F6c: STOCK / SERVICE / KIT; null for a free-text line. */
  itemKind?: string | null;
  /** G5b: on a foreign order, the entered unit price and the line net in the order currency. */
  foreignUnitPrice?: number | null; foreignAmount?: number | null;
}

export interface ChallanSummary {
  id: string; challanNo?: string; salesOrderId: string; orderNo?: string; customerId: string; customerName?: string;
  storeId: string; storeName?: string; challanDate: string; status: ChallanStatus; salesInvoiceId?: string; invoiceNumber?: string;
}

export interface SalesOrder {
  id: string; institutionId: string; orderNo?: string; customerId: string; customerName?: string; orderDate: string;
  requestedDeliveryDate?: string; quotationId?: string; status: SalesOrderStatus; subtotal: number; taxAmount: number;
  totalAmount: number; notes?: string; confirmedAt?: string; cancelReason?: string; fullyDelivered: boolean;
  fullyInvoiced: boolean; lines: OrderLine[]; challans: ChallanSummary[]; warnings?: string[];
  /** G5b: document currency; the rate is indicative (the invoice resolves its own). */
  currencyCode?: string; exchangeRate?: number | null; foreignSubtotal?: number | null; foreignTotal?: number | null;
  salespersonId?: string | null; salespersonName?: string | null; salesTeamId?: string | null;   // S0
}

export interface OrderSummary {
  id: string; orderNo?: string; customerId: string; customerName?: string; orderDate: string; requestedDeliveryDate?: string;
  status: SalesOrderStatus; totalAmount: number; fullyInvoiced: boolean; overdue: boolean;
  currencyCode?: string; foreignTotal?: number | null;   // G5b
  salespersonId?: string | null; salespersonName?: string | null;   // S0
}

export interface OrderPage { content: OrderSummary[]; totalElements: number; }

export interface ChallanLine {
  id: string; salesOrderLineId: string; itemId?: string; itemCode?: string; itemName?: string; unit?: string;
  description: string; quantity: number; unitPrice?: number; relievedValue?: number;
  /** F6c: what a posted kit line relieved per component (empty for other lines). */
  kitRelief?: KitRelief[];
}

export interface Challan {
  id: string; institutionId: string; challanNo?: string; salesOrderId: string; orderNo?: string; customerId: string;
  customerName?: string; storeId: string; storeName?: string; challanDate: string; vehicleNo?: string; driverName?: string;
  notes?: string; status: ChallanStatus; journalEntryId?: string; reversalJournalEntryId?: string; salesInvoiceId?: string;
  invoiceNumber?: string; postedAt?: string; cancelReason?: string; lines: ChallanLine[];
}

export interface ChallanRequest {
  salesOrderId: string; storeId: string; challanDate: string; vehicleNo?: string; driverName?: string; notes?: string;
  lines: { salesOrderLineId: string; quantity: number }[];
}

export interface BackOrderRow {
  orderId: string; orderNo?: string; customerId: string; customerName?: string; orderLineId: string; itemId?: string;
  itemCode?: string; itemName?: string; description: string; storeId?: string; storeName?: string; orderedQty: number;
  deliveredQty: number; backOrderQty: number; orderDate: string; requestedDeliveryDate?: string; ageDays: number;
  overdue: boolean; availableQty?: number | null;
}

export interface SalesOrderSettings {
  institutionId?: string; defaultStoreId?: string | null; showPricesOnChallan: boolean; allowInvoiceWithoutOrder: boolean;
}

export interface InvoiceFromChallansRequest {
  challanIds: string[]; issueDate: string; dueDate?: string; notes?: string;
  /** G5b: the invoice's own rate for a foreign-currency order (blank = the table rate on the issue date); ignored on BDT. */
  exchangeRate?: number | null;
}
export type InvoiceFromChallansResult = SalesInvoice;
